import { motion, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { STATS } from "@/lib/site-data";

function Counter({ value }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-20%" });
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!inView) return;
    const numericMatch = value.match(/([0-9]+\.?[0-9]*)/);
    if (!numericMatch) { setDisplay(value); return; }
    const target = parseFloat(numericMatch[1]);
    const suffix = value.replace(numericMatch[1], "");
    let start = 0;
    const duration = 1400;
    const startTime = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - startTime) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = (start + (target - start) * eased);
      const rendered = target % 1 === 0 ? Math.round(v).toString() : v.toFixed(1);
      setDisplay(rendered + suffix);
      if (p < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }, [inView, value]);

  return <span ref={ref}>{display}</span>;
}

export default function Stats() {
  return (
    <section data-testid="stats-section" className="relative bg-ab-primary text-white py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 ab-grain opacity-30" />
      <div className="ab-container relative">
        <div className="max-w-3xl">
          <p className="ab-eyebrow text-ab-accent">Outcomes, not promises</p>
          <h2 className="ab-h2 text-5xl md:text-6xl mt-4 text-white">
            Numbers from real <span className="italic font-light">hospitals</span>.
          </h2>
        </div>
        <div className="mt-16 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-3xl overflow-hidden border border-white/10">
          {STATS.map((s, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.7 }}
              className="bg-ab-primary p-8 md:p-12"
              data-testid={`stat-${i}`}
            >
              <p className="font-serif text-6xl md:text-7xl tracking-tight text-white">
                <Counter value={s.value} />
              </p>
              <p className="mt-4 text-white/65 text-sm md:text-base leading-relaxed">{s.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
